import { BEM } from '@feedfm/feed-util';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';

import './_ContentSourceDetailBasics.scss';
import { ContentSourceDetailsFragment } from '~/graphqlTypes/ContentSourceDetailsFragment';

export interface ContentSourceDetailsBasicsProps {
  source: ContentSourceDetailsFragment;
  isSaving: boolean;
  handleSave: ( newPricePerPlay: number ) => void;
}

// This aligns with the backend.
const allowedPrecision = 5;

export const ContentSourceDetailBasics: React.FC<ContentSourceDetailsBasicsProps> = ( props ) => {
  const { handleSave, isSaving, source } = props;

  const [clean, setClean] = useState( true );
  const [localSource, setLocalSource] = useState<ContentSourceDetailsFragment>( source );
  const [pricePerPlayRaw, setPricePerPlayRaw] = useState( source.pricePerPlay.toString() );
  const [validated, setValidated] = useState( false );

  const bem = new BEM( 'ops-catalog-sources-detail-basics', [] );
  const pricePerPlayFloat = parseFloat( pricePerPlayRaw );

  // When localSource changes, update clean state.
  useEffect(
    () => setClean( source.pricePerPlay === pricePerPlayFloat ),
    [source, localSource, pricePerPlayFloat],
  );

  const handleUpdatePricePerPlay = ( event: React.ChangeEvent<HTMLInputElement> ) =>
    setPricePerPlayRaw( event.target.value );

  const handleUpdateDDEXPartyId = ( event: React.ChangeEvent<HTMLInputElement> ) =>
    setLocalSource( { ...localSource, ddexPartyId: event.target.value } );

  const handleFormSubmit = useCallback(
    ( event: React.FormEvent<HTMLFormElement> ) => {
      event.preventDefault();

      if ( event.currentTarget.checkValidity() === true ) {
        handleSave( pricePerPlayFloat );
      }

      setValidated( true );
    },
    [handleSave, pricePerPlayFloat],
  );

  return (
    <section className={ BEM.$( bem ) } data-testid="ops-catalog-sources-detail-basics">
      <h3>General Info</h3>
      <div className={ BEM.$( bem.el( 'inner' ) ) }>
        <div className={ BEM.$( bem.el( 'left' ) ) }>
          <Form onSubmit={ handleFormSubmit } validated={ validated } noValidate>
            <Form.Group controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control value={ localSource.name } disabled />
            </Form.Group>
            { source.ddexPartyId && (
              <Form.Group controlId="ddex-party-id">
                <Form.Label>DDEX Party ID</Form.Label>
                <Form.Control value={ localSource.ddexPartyId || '' } onChange={ handleUpdateDDEXPartyId } disabled />
              </Form.Group>
            ) }
            <Form.Group controlId="price_per_play">
              <Form.Label>Price per play (USD)</Form.Label>
              <Form.Control
                value={ pricePerPlayRaw }
                onChange={ handleUpdatePricePerPlay }
                type="number"
                min={ 0 }
                max={ 0.99999 }
                step={ 0.00001 }
                disabled
              />
              <Form.Control.Feedback type="invalid">
                Price per play must be a number between 0 and 0.99999999, with no more than { allowedPrecision } decimal
                places.
              </Form.Control.Feedback>
            </Form.Group>
            <Button type="submit" variant="primary" disabled={ clean || isSaving }>
              { !clean && isSaving && (
                <>
                  <Spinner as="span" size="sm" role="status" aria-hidden="true" animation="border" />{ ' ' }
                </>
              ) }
              Save
            </Button>
          </Form>
        </div>
      </div>
    </section>
  );
};
